import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  postProductCompare,
  getScannerPrefs,
  type ProductCompareResponse,
  type ProductPairIds,
} from '../api/endpoints';
import { isIngredientsPendingError, isSameProductError } from '../api/pairErrors';
import { isCreditError } from '../api/config';
import useScanCredits from '../hooks/useScanCredits';
import { IconBalance, IconCompare, IconHelp, IconSwap, IconGear } from '../components/icons/AnalyzerIcons';
import WaitingGame from '../components/WaitingGame';
import ModalShell from '../components/ModalShell';
import type { ScreenProps } from '../types/screen';
import { getErrorMessage } from '../utils/errors';
import {
  createProductReference,
  readComparisonDraft,
  writeComparisonDraft,
  type ProductPairTransfer,
} from '../utils/productIdentity';
import ComparePreferencesPage from './shared/ComparePreferencesPage';
import ProductSideFlow from './shared/productSideFlow/ProductSideFlow';
import { NOT_ENOUGH_CREDITS_MESSAGE } from './shared/productSideFlow/useProductSideFlow';
import { pendingBarcode, referenceFromSide, sideFromReference } from './shared/productSideFlow/sideProducts';
import { CreditPill, OutOfCreditsCard, usePairSelection, type PairSide } from './shared/pairScreen';
import { AnimatedSwap, pageVariants, sectionItem, spring } from './shared/screenMotion';

export interface ProductCompareInput {
  products?: ProductPairTransfer['products'];
  prefill?: { barcodeA?: string; barcodeB?: string; productIdA?: number; productIdB?: number };
  /** Set by the result page after a 409: that side's ingredients are pending. */
  pendingSide?: PairSide;
}

export interface ProductCompareResult {
  response: ProductCompareResponse;
  payload: ProductPairIds;
  productIds: { a?: number; b?: number };
}

function initialReferences(input: ProductCompareInput | undefined, mode: 'page' | 'modal') {
  const prefill = input?.prefill ?? {};
  const draft = mode === 'page' ? readComparisonDraft()?.products : undefined;
  const fromPrefill = (productId?: number, barcode?: string) =>
    productId || barcode ? createProductReference({ productId, barcode }) : undefined;
  const a = input?.products?.[0] ?? fromPrefill(prefill.productIdA, prefill.barcodeA) ?? draft?.[0];
  const b = input?.products?.[1] ?? fromPrefill(prefill.productIdB, prefill.barcodeB) ?? draft?.[1];
  return {
    initialA: sideFromReference(a, draft?.[0]),
    initialB: sideFromReference(b, draft?.[1]),
    barcodeA: pendingBarcode(a),
    barcodeB: pendingBarcode(b),
  };
}

function usePrefsPrompt(mode: 'page' | 'modal', open: () => void) {
  useEffect(() => {
    if (mode !== 'page') return;
    void (async () => {
      try {
        const p = await getScannerPrefs();
        const looksDefault =
          p.allergies.length === 0 &&
          p.sensitivity === 'likely' &&
          !p.acne_prone &&
          p.ethical_mode === 'off' &&
          !p.cautious_actives;
        const shown = localStorage.getItem('prefsPagePromptShownCompare');
        if (looksDefault && !shown) {
          localStorage.setItem('prefsPagePromptShownCompare', '1');
          open();
        }
      } catch { /* ignore */ }
    })();
  }, [mode, open]);
}

const ProductCompareScreen: React.FC<ScreenProps<ProductCompareInput, ProductCompareResult>> = ({
  mode,
  input,
  onComplete,
  onCancel,
}) => {
  const nav = useNavigate();
  const [initial] = useState(() => initialReferences(input, mode));
  const pair = usePairSelection(initial.initialA, initial.initialB, input?.pendingSide);
  const { productA, productB } = pair;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrefs, setShowPrefs] = useState(false);
  const [gameVisible, setGameVisible] = useState(false);
  const gameTimerRef = useRef<number | null>(null);
  const openPrefs = useCallback(() => { setShowPrefs(true); }, []);
  usePrefsPrompt(mode, openPrefs);

  const { total: creditsLeft, cost, outOfQuota, refreshCredits } = useScanCredits('product_compare');

  useEffect(() => {
    if (mode !== 'page') return;
    writeComparisonDraft([referenceFromSide(productA), referenceFromSide(productB)]);
  }, [mode, productA, productB]);

  useEffect(() => () => {
    if (gameTimerRef.current) window.clearTimeout(gameTimerRef.current);
  }, []);

  const submit = async () => {
    if (!pair.canSubmit || !productA || !productB || loading || outOfQuota) return;
    setLoading(true);
    setError(null);
    if (gameTimerRef.current) window.clearTimeout(gameTimerRef.current);
    gameTimerRef.current = window.setTimeout(() => { setGameVisible(true); }, 600);

    const payload: ProductPairIds = { product_id_a: productA.productId, product_id_b: productB.productId };
    try {
      const response = await postProductCompare(payload);
      void refreshCredits();
      const productIds = { a: productA.productId, b: productB.productId };
      setGameVisible(false);
      setLoading(false);
      if (mode === 'modal') onComplete?.({ response, payload, productIds });
      else nav('/comparison/result', { state: { response, payload, productIds } });
    } catch (error_: unknown) {
      if (isIngredientsPendingError(error_)) {
        // The side itself says its ingredients are pending; no second message here.
        pair.markPending(error_.side);
      } else if (isSameProductError(error_)) {
        setError('Choose two different products to compare.');
      } else if (isCreditError(error_)) {
        setError(error_.status === 402 ? NOT_ENOUGH_CREDITS_MESSAGE : error_.message);
        void refreshCredits();
      } else {
        setError(getErrorMessage(error_, 'Failed to compare products'));
      }
      setGameVisible(false);
      setLoading(false);
    } finally {
      if (gameTimerRef.current) window.clearTimeout(gameTimerRef.current);
    }
  };

  const prefersReduced = useReducedMotion() ?? false;
  const [swapAngle, setSwapAngle] = useState(0);
  const handleSwap = () => {
    pair.swap();
    setSwapAngle((a) => a + 180);
  };

  /** The pair is complete and affordable: the submit button is live. */
  const canRun = pair.canSubmit && !outOfQuota;
  const notice = error ?? (pair.duplicate ? 'Choose two different products to compare.' : null);
  const cta = (
    <>
      <IconCompare />
      <AnimatedSwap value={loading ? 'Comparing…' : 'Compare products'} />
    </>
  );

  const sides = (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] xl:gap-5">
      <ProductSideFlow
        key={`a-${pair.sideKey}`}
        side="a"
        context="compare"
        label="Product A"
        initial={pair.initialA}
        initialBarcode={pair.sideKey === 0 ? initial.barcodeA : undefined}
        disabledProductId={productB?.productId}
        pendingMarked={pair.isPending('a')}
        onChange={pair.onChange('a')}
      />
      <div className="flex items-center justify-center">
        <div className="stack-2 items-center w-full xl:w-auto">
          <motion.button
            type="button"
            whileTap={{ scale: 0.96 }}
            onClick={handleSwap}
            disabled={!pair.canSwap || loading}
            className="btn btn--ghost btn--sm row-3 w-full xl:w-auto justify-center"
          >
            <motion.span animate={{ rotate: swapAngle }} transition={{ type: 'spring', stiffness: 320, damping: 28 }} style={{ display: 'inline-flex' }}>
              <IconSwap />
            </motion.span>
            Swap A/B
          </motion.button>
          <p className="t-small t-mute text-center">A versus B</p>
        </div>
      </div>
      <ProductSideFlow
        key={`b-${pair.sideKey}`}
        side="b"
        context="compare"
        label="Product B"
        initial={pair.initialB}
        initialBarcode={pair.sideKey === 0 ? initial.barcodeB : undefined}
        disabledProductId={productA?.productId}
        pendingMarked={pair.isPending('b')}
        onChange={pair.onChange('b')}
      />
    </div>
  );

  const inner = (
    <AnimatePresence mode="wait" initial={false}>
      {showPrefs ? (
        <motion.div
          key="prefs"
          initial={{ opacity: 0, x: prefersReduced ? 0 : 32 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: prefersReduced ? 0 : 32 }}
          transition={spring}
        >
          <ComparePreferencesPage onBack={() => { setShowPrefs(false); }} />
        </motion.div>
      ) : (
        <motion.div key="main" className="stack-4" variants={pageVariants} initial="hidden" animate="visible">
          <motion.div variants={sectionItem} className="card card--bordered relative overflow-hidden">
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'linear-gradient(135deg, var(--c-teal-weak) 0%, transparent 50%, var(--c-teal-weak) 100%)' }}
            />
            <div className="relative">
              <div className="row-3 mb-3">
                <div
                  className="grid place-items-center w-9 h-9 rounded-full shrink-0"
                  style={{ background: 'var(--c-teal-weak)', color: 'var(--c-kivo-teal)' }}
                >
                  <IconBalance />
                </div>
                <CreditPill cost={cost} left={creditsLeft} outOfQuota={outOfQuota} />
                <button type="button" onClick={() => { setShowPrefs(true); }} className="btn btn--ghost btn--sm ml-auto row-3">
                  <IconGear /> Preferences
                </button>
              </div>
              <h1 className="t-h3">Product comparison</h1>
              <p className="t-small t-mute mt-1">
                Scan, photograph or pick two products. We&apos;ll analyze them side-by-side and suggest the better fit.
              </p>
              {mode === 'page' && (
                <button
                  type="button"
                  onClick={() => { nav('/scanner/how-it-works'); }}
                  className="mt-2 inline-flex items-center gap-1 t-small"
                  style={{ color: 'var(--c-kivo-teal)', textDecoration: 'underline' }}
                >
                  <IconHelp /> Learn how this works
                </button>
              )}
            </div>
          </motion.div>

          <motion.div variants={sectionItem} className="card card--bordered stack-4">
            {/* The sides stay mounted when credits run out, so a charged scan is never lost. */}
            <>
                {sides}
                {notice && (
                  <p
                    className="t-small"
                    role="alert"
                    style={{ color: 'var(--c-error)', background: 'rgba(196,103,58,0.08)', border: '1px solid rgba(196,103,58,0.2)', borderRadius: 'var(--r-sm)', padding: '8px 12px' }}
                  >
                    {notice}
                  </p>
                )}
                {/* Only a ready pair can be short of credits: the pill covers the rest. */}
                {pair.canSubmit && outOfQuota && (
                  <OutOfCreditsCard
                    closeLabel={mode === 'modal' ? 'Close' : 'Back'}
                    onBuy={() => { nav('/wallet'); }}
                    onClose={() => { if (mode === 'modal') onCancel?.(); else nav('/'); }}
                  />
                )}
                {canRun && (
                  <div className="compare-ready" role="status">
                    <span className="dot" /> Ready to compare
                  </div>
                )}
                <div className={mode === 'page' ? 'hidden md:flex md:flex-wrap gap-2' : 'flex flex-col sm:flex-row sm:flex-wrap gap-2'}>
                  <motion.button
                    type="button"
                    whileTap={{ scale: prefersReduced ? 1 : 0.97 }}
                    onClick={submit}
                    disabled={!pair.canSubmit || loading || outOfQuota}
                    className="btn btn--primary row-3 w-full sm:w-auto justify-center"
                  >
                    {cta}
                  </motion.button>
                </div>
            </>
          </motion.div>

          {mode === 'page' && (
            <div className="compare-mobile-cta md:hidden" data-testid="compare-mobile-cta">
              <div className="t-small" style={{ fontWeight: 600, color: canRun ? 'var(--c-deep-teal)' : 'var(--ink-muted)' }}>
                {canRun ? 'Ready to compare' : pair.canSubmit ? 'Top up to continue' : 'Select two different products'}
              </div>
              <button type="button" onClick={submit} disabled={!pair.canSubmit || loading || outOfQuota} className="btn btn--primary row-3 w-full justify-center">
                {cta}
              </button>
            </div>
          )}

          {gameVisible && loading && <WaitingGame onExit={() => { setGameVisible(false); }} />}
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (mode === 'modal') {
    return (
      <ModalShell title="Compare products" onClose={() => onCancel?.()} widthClass="max-w-4xl">
        <div className="p-4 sm:p-5">{inner}</div>
      </ModalShell>
    );
  }

  return inner;
};

export default ProductCompareScreen;
